import React, { Suspense, useEffect } from "react";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import ReactDOM from "react-dom/client";
import "@fontsource-variable/source-sans-3";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import ErrorPage from "./ErrorPage";
import Login from "./features/onboarding/Login";
import Settings from "./features/settings/Settings";
import SettingsWebview from "./features/settings/SettingsWebview";
import About from "./features/settings/About";
import CovidPolicy from "./features/settings/CovidPolicy";
import Discover from "./features/discover/Discover";
import { Squirrel } from "./components/spinner/Squirrel";
import { loader as homeLoader } from "./features/home/Home";
import { createBrowserRouter, Outlet, RouterProvider, useOutletContext } from "react-router-dom";

// Layout component that forwards outlet context to nested routes
function PassthroughLayout() {
  const context = useOutletContext();
  return <Outlet context={context} />;
}

// import i18n (needs to be bundled ;))
import "./i18n";
import Records from "./features/immunizations/Records";
import SearchList from "./features/immunizations/SearchList";
import Home from "./features/home/Home";
import AltContactsForm from "./features/immunizations/AltContactsForm";
import EnterPinForm from "./features/immunizations/EnterPinForm";
import RequiresAuth from "./components/RequiresAuth";
import DefferedAction from "./features/sw_elements/DefferedAction";
import OfflineBanner from "./features/sw_elements/Offline";
import EnterPhonePin from "./features/onboarding/EnterPhonePin";
import VerifyPhone from "./features/onboarding/VerifyPhone";
import { STAGE } from "./globals";
import VERSION from "./version";
import SignUpTemplate from "./features/onboarding/SignUpTemplate";
import { NotFound } from "./components/NotFound";
import AccountInfo from "./features/onboarding/AccountInfo";
import { CalendarAdd } from "./features/calendar-add";
import BrandingProvider from "./branding/BrandingProvider";
import ThemeWrapper from "./features/ThemeWrapper";
import i18n, { initializeI18next } from "./i18n";
import { globalStore } from "./jotai/store";
import { Provider } from "jotai";
import DeepLinkListener from "./features/DeepLinkListener";
import BiometricsLockout from "./features/BiometricsLockout";
import NativeStatusBar from "./features/NativeStatusBar";
import { initializePushNotificationListeners } from "./pushNotifications";
import { Capacitor } from "@capacitor/core";
import { TextZoom } from "@capacitor/text-zoom";
import { App } from "@capacitor/app";
import "./app.css";
import { faqLink } from "./utils/dockethealth-links";
import { appVersion } from "./utils/app-version";

const getAppType = () => {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") {
    return "iOS";
  } else if (platform === "android") {
    return "Android";
  }
  return "Browser";
};

async function initFontSizeZoom() {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  const { value } = await TextZoom.getPreferred();
  /**
   * Cap the zoom between 100% and 125%. Values beyond 150% make the UI unusable since, in addition to scaling up the
   * text size, this also scales up the em/rem units used in our padding/margins/etc. Values below 100% can trigger
   * iOS auto-zoom behavior on text inputs.
   */
  document.documentElement.style.fontSize = `${Math.max(Math.min(value * 100, 125), 100)}%`;
}

Bugsnag.start({
  apiKey: Capacitor.isNativePlatform()
    ? "231db42287ec10ab6030f410210c3de2" // Docket PWA Capacitor  project
    : "d521ee05a3871fc096427c05e2b6ceac", // Docket PWA project
  plugins: [new BugsnagPluginReact()],
  appVersion: await appVersion(),
  appType: getAppType(),
  redactedKeys: [/password/i, /token/i, /name/i, /dob/i, /phone/i, /title/i],
  releaseStage: STAGE,
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();
await initializeI18next();
/*
 * Inject the lang attribute into the HTML document element after i18n is initialized. We hardcoded "en" in the <html>
 * tag in index.html before this, but that would cause i18n to always default to English if the navigator.languages
 * didn't have an exact match with our supported languages. For example `it-CH` would cause `en` to be used, not `it`.
 * I'm not sure we even need to inject this (i18n and the translations work without it), but it seems like a good idea.
 * Should help things like screen readers.
 */
document.documentElement.lang = i18n.language;
await initializePushNotificationListeners();

// Initialize font size zooming on startup (we will also attach a listener to do this when the app state becomes active)
await initFontSizeZoom();

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <>
        <DeepLinkListener />
        <Login />
      </>
    ),
    errorElement: <NotFound />,
    action: async ({ params, request }) => {
      let formData = await request.formData();
      return {};
    },
  },
  {
    path: "/biometrics",
    element: <BiometricsLockout />,
  },
  {
    path: "/signin-link",
    element: <Login />,
  },
  {
    path: "/apple-auth/callback",
    element: <Login />,
  },
  {
    path: "/signup",
    element: (
      <RequiresAuth>
        <SignUpTemplate />
      </RequiresAuth>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: "accountInfo",
        element: <AccountInfo />,
      },
      {
        path: "verifyphone",
        element: <VerifyPhone autoFocusInput />,
      },
      {
        path: "enterpin",
        element: <EnterPhonePin autoFocusInput />,
      },
    ],
  },
  {
    path: "/add-event",
    element: <CalendarAdd />,
  },
  {
    path: "/home",
    element: (
      <RequiresAuth>
        <Home />
      </RequiresAuth>
    ),
    errorElement: <ErrorPage />,
    loader: homeLoader,
    children: [
      {
        path: "search",
        element: <PassthroughLayout />,
        children: [
          {
            index: true,
            element: <SearchList />,
          },
          {
            path: "altcontacts",
            element: <AltContactsForm />,
          },
          {
            path: "enterpin",
            element: <EnterPinForm />,
          },
        ],
      },
      {
        path: "records",
        element: <PassthroughLayout />,
        children: [
          {
            index: true,
            element: <Records recordType="immunization" />,
          },
          {
            path: "screening",
            element: <Records recordType="screening" />,
          },
        ],
      },
      {
        path: "settings",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            index: true,
            element: <Settings />,
          },
        ],
      },
      {
        path: "about",
        element: <About />,
      },
      {
        path: "covid-policy",
        element: <CovidPolicy />,
      },
      {
        path: "records-policy",
        element: <SettingsWebview url={faqLink(i18n.language)} />,
      },
      {
        path: "privacy",
        element: <SettingsWebview url="https://dockethealth.com/privacy_policy" />,
      },
      {
        path: "terms",
        element: <SettingsWebview url="https://dockethealth.com/terms_of_use" />,
      },
      {
        path: "feedback",
        element: <SettingsWebview url="https://www.surveymonkey.com/r/259TTMJ" />,
      },
      {
        path: "discover",
        element: <Discover />,
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// https://docs.bugsnag.com/platforms/javascript/react/
const ErrorBoundary = Bugsnag.getPlugin("react")!.createErrorBoundary(React);

function Loading() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <Squirrel />
    </div>
  );
}

/**
 * When users return to the app, we should set the font size zoom based on the user's preference in case they just
 * changed their accessibility settings.
 */
function TextZoomHandler() {
  useEffect(() => {
    const listener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        initFontSizeZoom().catch((e) => {
          Bugsnag.notify(e);
        });
      }
    });
    return () => {
      listener
        .then((l) => l.remove())
        .catch((e) => {
          Bugsnag.notify(e);
        });
    };
  }, []);
  return null;
}

root.render(
  <ErrorBoundary FallbackComponent={NotFound}>
    <React.StrictMode>
      <Provider store={globalStore}>
        <BrandingProvider>
          <ThemeWrapper>
            <Suspense fallback={<Loading />}>
              <DefferedAction />
              <OfflineBanner />
              <NativeStatusBar />
              <TextZoomHandler />
              <RouterProvider router={router} fallbackElement={<Loading />} />
            </Suspense>
          </ThemeWrapper>
        </BrandingProvider>
      </Provider>
    </React.StrictMode>
  </ErrorBoundary>
);
